import classnames from 'classnames';
import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import dibsCss from 'dibs-css';
import { FormattedMessage, FormattedNumber, useIntl } from 'dibs-react-intl';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import StarFilled from 'dibs-icons/exports/legacy/StarFilled';
import Vetting from 'dibs-icons/exports/Vetting';
import { getIsNewSeller } from '../../utils/sellerHelpers';
import { useSellerRating } from '../../shared/hooks/useSellerRating';
import { PdpSharedNewSellerPill } from '../shared/PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { vettedSeller, vettedTooltip } from '../messages/pdpDistinctionsMessages';

// types
import { type PdpSharedMeetTheSellerHeader_seller$key as Seller } from './__generated__/PdpSharedMeetTheSellerHeader_seller.graphql';

import styles from './PdpSharedMeetTheSellerHeader.module.css';

const MINIMUM_ORDER_QUANTITY = 5;

const sellerFragment = graphql`
    fragment PdpSharedMeetTheSellerHeader_seller on Seller {
        ...useSellerRating_seller
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        sellerOrderQuantity
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                    }
                }
            }
        }
        orderFeedbackPdp {
            metadata {
                aggregatedFeedback {
                    publicNoteTotalResults
                }
            }
        }
    }
`;

export const PdpSharedMeetTheSellerHeader: FC<{
    seller: Seller;
    isMobile?: boolean;
    variant?: 'default' | 'noReviews';
}> = ({ seller: sellerRef, isMobile, variant = 'default' }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { formatNumber } = useIntl();
    const { isRewardsTier2, monthsSinceStartDate, liveStorefrontProfile, sellerOrderQuantity } =
        seller || {};
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];

    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find((media: $TSFixMe) => media?.element?.path)?.element?.path;
    }

    const newSellerPill = isNewSeller ? (
        <span className={classnames(dibsCss.mlXsmall)}>
            <PdpSharedNewSellerPill backgroundColor="white" unsetTextSize />
        </span>
    ) : null;

    const sellerImage = sellerImagePath ? (
        <img
            alt="seller"
            src={sellerImagePath}
            className={classnames(dibsCss.roundedFull, styles.sellerImage)}
            srcSet={getSrcsetString(sellerImagePath, [200])}
        />
    ) : null;

    const rating = useSellerRating({ seller });
    const reviewCount =
        seller?.orderFeedbackPdp?.metadata?.aggregatedFeedback?.publicNoteTotalResults || 0;

    return (
        <>
            <div
                className={classnames(dibsCss.flex, dibsCss.gapMedium, dibsCss.itemsCenter, {
                    [dibsCss.gapSmall]: isMobile,
                })}
            >
                {sellerImage && <div className={dibsCss.flexNone}>{sellerImage}</div>}
                <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXxsmall)}>
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.gapXxsmall
                        )}
                    >
                        {reviewCount > 0 && variant !== 'noReviews' ? (
                            <>
                                <StarFilled
                                    className={classnames(
                                        dibsCss.w14px,
                                        dibsCss.h14px,
                                        dibsCss.fillTangerine500
                                    )}
                                />
                                <span className={dibsCss.sassyFontBodyMediumHeavy}>
                                    <FormattedNumber
                                        style="decimal"
                                        value={rating}
                                        minimumFractionDigits={1}
                                    />
                                    {` (`}
                                    <FormattedNumber style="decimal" value={reviewCount} />
                                    {`)`}
                                </span>
                                {newSellerPill}
                            </>
                        ) : (
                            <div
                                className={classnames(dibsCss.sassyFontBodySmall, dibsCss.textNoir)}
                            >
                                <div
                                    className={classnames(
                                        dibsCss.fillShagreen800,
                                        dibsCss.flex,
                                        dibsCss.itemsCenter,
                                        dibsCss.mbXxsmall
                                    )}
                                >
                                    <Vetting variation="20" />
                                    <span
                                        className={classnames(
                                            dibsCss.sassyFontBodySmallHeavy,
                                            dibsCss.mlXsmall
                                        )}
                                    >
                                        {vettedSeller}
                                    </span>
                                    {!isMobile && newSellerPill}
                                </div>
                                {isMobile && isNewSeller && (
                                    <div
                                        className={classnames(dibsCss.mbXxsmall, dibsCss.mtXsmall)}
                                    >
                                        <PdpSharedNewSellerPill
                                            backgroundColor="white"
                                            unsetTextSize
                                        />
                                    </div>
                                )}
                                <div>{vettedTooltip}</div>
                                <div
                                    className={classnames(
                                        dibsCss.flex,
                                        dibsCss.itemsCenter,
                                        dibsCss.mtXsmall
                                    )}
                                >
                                    <StarFilled
                                        className={classnames(
                                            dibsCss.w14px,
                                            dibsCss.h14px,
                                            dibsCss.fillIron,
                                            dibsCss.mrXxsmall
                                        )}
                                    />
                                    <span data-tn="dealer-no-reviews">
                                        <FormattedMessage
                                            id="PdpSharedMeetTheSellerHeader.noReviews"
                                            defaultMessage="No reviews yet"
                                        />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {variant !== 'noReviews' &&
                        sellerOrderQuantity &&
                        sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
                            <div
                                className={dibsCss.sassyFontBodyMedium}
                                data-tn="dealer-orderQuantity"
                            >
                                <FormattedMessage
                                    id="PdpSharedMeetTheSellerHeader.sellerOrderQuantity"
                                    defaultMessage="{sellerOrderQuantity} Sales on 1stDibs"
                                    values={{
                                        sellerOrderQuantity: formatNumber(sellerOrderQuantity),
                                    }}
                                />
                            </div>
                        )}
                </div>
            </div>
        </>
    );
};
